import React, { useEffect, useState, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { 
    Container, Grid, Typography, Box, Button, Paper, Divider, 
    Skeleton 
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faArrowRight, faShoppingBag, faWallet, 
    faCheckCircle, faExclamationCircle 
} from '@fortawesome/free-solid-svg-icons';

// Componentes y Controladores
import CartItemRow from '../../../components/commerce/CartItemRow';
import { getMyCart, removeCartItem, updateCartItemQty } from '../../../controllers/commerce/CartController';
import CustomBreadcrumbs from '../../../components/breadcrumbs';
// import { getWalletBalance } from '../../../controllers/customer/WalletController'; // <-- Futuro controlador

export default function CartPage() {
    const history = useHistory();
    
    const [cartData, setCartData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    
    // --- NUEVO ESTADO PARA LA WALLET ---
    const [walletBalance, setWalletBalance] = useState(0.00); 

    useEffect(() => {
        // Cargamos ambas cosas al montar el componente
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        
        // 1. Cargar Carrito
        const resCart = await getMyCart();
        if (resCart.success && resCart.data) {
            setCartData(resCart.data);
        } else {
            setCartData(null);
        }

        // 2. Cargar Balance (Simulado por ahora)
        // const resWallet = await getWalletBalance();
        // if(resWallet.success) setWalletBalance(resWallet.data.balance);
        setWalletBalance(0.00); // <--- HARDCODED POR AHORA

        setLoading(false);
    };

    // Acción Eliminar
    const handleRemoveItem = async (itemId) => {
        if(!window.confirm("¿Eliminar este producto?")) return;
        setProcessing(true);
        const res = await removeCartItem(itemId);
        if (res.success) {
            // Recargamos solo el carrito, el balance no debería cambiar por borrar items
            const updatedCart = await getMyCart();
            if (updatedCart.success) setCartData(updatedCart.data);
        }
        setProcessing(false);
    };

    // Acción Actualizar Cantidad
    const handleUpdateQty = async (itemId, newQty) => {
        const res = await updateCartItemQty(itemId, newQty);
        if (res.success) {
            const updatedCart = await getMyCart();
            if (updatedCart.success) setCartData(updatedCart.data);
        }
    };

    const handleCheckout = () => {
        history.push('/checkout');
    };

    // --- RENDERIZADO DE CARGA ---
    if (loading && !cartData) {
        return (
            <Container maxWidth="xl" sx={{ pt: 12 }}>
                <CustomBreadcrumbs returnTo='/' crumbs={[{ name: 'Carrito', link: '/cart' }]} />
                <Grid container spacing={4} mt={2}>
                    <Grid item xs={12} md={8}><Skeleton variant="rectangular" height={100} sx={{ borderRadius: 3 }} /></Grid>
                    <Grid item xs={12} md={4}><Skeleton variant="rectangular" height={300} sx={{ borderRadius: 3 }} /></Grid>
                </Grid>
            </Container>
        );
    }

    // Datos del Carrito
    const items = cartData?.items || [];
    const summary = cartData?.summary || { subtotal: 0, total: 0 };

    // --- LÓGICA DE WALLET ---
    const currentBalance = parseFloat(walletBalance);
    const totalToPay = parseFloat(summary.total);
    const remainingBalance = currentBalance - totalToPay;
    const isSufficient = remainingBalance >= 0;

    return (
        <Container maxWidth="xl" sx={{ pb: 8 }}>
            <Box mb={4}>
                <CustomBreadcrumbs returnTo='/' crumbs={[{ name: 'Carrito', link: '/cart' }]} />
                <Typography variant="h4" fontWeight="bold" sx={{ mt: 2 }}>
                    Mi Carrito {items.length > 0 && `(${items.length})`}
                </Typography>
            </Box>

            {items.length === 0 ? (
                // --- ESTADO VACÍO ---
                <Paper sx={{ p: 8, textAlign: 'center', bgcolor: '#f9fafb', borderRadius: 4 }} elevation={0}>
                    <Box sx={{ color: 'text.disabled', mb: 3 }}>
                        <FontAwesomeIcon icon={faShoppingBag} size="4x" />
                    </Box>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>Tu carrito está vacío</Typography>
                    <Button 
                        variant="contained" size="large"
                        sx={{ borderRadius: 50, px: 5, mt: 2 }}
                        onClick={() => history.push('/')}
                    >
                        Comenzar a Comprar
                    </Button>
                </Paper>
            ) : (
                <Grid container spacing={4}>
                    
                    {/* --- LISTA DE ITEMS (IZQUIERDA) --- */}
                    <Grid item xs={12} md={8}>
                        {items.map((item) => (
                            <CartItemRow 
                                key={item.item_id}
                                item={item} 
                                onRemove={handleRemoveItem}
                                onUpdateQty={handleUpdateQty}
                            />
                        ))}
                    </Grid>

                    {/* --- RESUMEN DE PAGO + WALLET INFO (DERECHA) --- */}
                    <Grid item xs={12} md={4}>
                        <Paper 
                            elevation={0} 
                            sx={{ 
                                p: 3, 
                                bgcolor: '#fff', 
                                border: '1px solid', borderColor: 'divider', borderRadius: 3, 
                                position: 'sticky', top: 100
                            }}
                        >
                            <Typography variant="h6" fontWeight="bold" mb={3}>Resumen</Typography>
                            
                            <Box display="flex" justifyContent="space-between" mb={2}>
                                <Typography color="text.secondary">Subtotal</Typography>
                                <Typography fontWeight="600">${summary.subtotal.toFixed(2)}</Typography>
                            </Box>
                            
                            <Divider sx={{ my: 2 }} />

                            <Box display="flex" justifyContent="space-between" mb={3} alignItems="flex-end">
                                <Typography variant="h6" fontWeight="bold">Total</Typography>
                                <Box textAlign="right">
                                    <Typography variant="caption" color="text.secondary">USD</Typography>
                                    <Typography variant="h4" fontWeight="bold" color="primary.main" sx={{ lineHeight: 1 }}>
                                        ${summary.total.toFixed(2)}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* --- CAJA DE INFORMACIÓN DE WALLET --- */}
                            <Box sx={{ bgcolor: '#f8f9fa', p: 2, borderRadius: 2, mb: 3, border: '1px dashed #e0e0e0' }}>
                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                    <FontAwesomeIcon icon={faWallet} color="#555" />
                                    <Typography variant="subtitle2" fontWeight="bold">Tu Saldo Disponible</Typography>
                                </Box>
                                
                                <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                                    ${currentBalance.toFixed(2)}
                                </Typography>

                                {isSufficient ? (
                                    // CASO A: TIENE SALDO
                                    <Box display="flex" alignItems="start" gap={1} color="success.main">
                                        <FontAwesomeIcon icon={faCheckCircle} style={{ marginTop: 3 }} />
                                        <Typography variant="caption" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                                            Tienes saldo suficiente. 
                                            <br/>
                                            Te quedarán: ${remainingBalance.toFixed(2)}
                                        </Typography>
                                    </Box>
                                ) : (
                                    // CASO B: FALTA SALDO
                                    <Box>
                                        <Box display="flex" alignItems="center" gap={1} color="warning.main" mb={0.5}>
                                            <FontAwesomeIcon icon={faExclamationCircle} />
                                            <Typography variant="caption" fontWeight="bold">
                                                Saldo insuficiente
                                            </Typography>
                                        </Box>
                                        <Typography variant="caption" display="block" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                                            Deberás recargar <strong>${Math.abs(remainingBalance).toFixed(2)}</strong> al confirmar el pedido.
                                        </Typography>
                                    </Box>
                                )}
                            </Box>

                            <Button 
                                fullWidth 
                                variant="contained" 
                                size="large"
                                onClick={handleCheckout}
                                disabled={processing}
                                endIcon={!processing && <FontAwesomeIcon icon={faArrowRight} />}
                                sx={{ 
                                    py: 1.8, borderRadius: 3, fontWeight: 'bold', fontSize: '1.05rem',
                                    boxShadow: '0 8px 20px -4px rgba(0,118,255,0.4)'
                                }}
                            >
                                {processing ? 'Cargando...' : 'Continuar'}
                            </Button>

                            <Typography variant="caption" display="block" textAlign="center" mt={2} color="text.secondary">
                                Confirmarás tus datos de envío en el siguiente paso.
                            </Typography>
                        </Paper>
                    </Grid>

                </Grid>
            )}
        </Container>
    );
}